import { Bike, Camera, Car, KeyRound, MapPin } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const points = [
  {
    icon: MapPin,
    title: "Enter via the rear laneway — Eden Street",
    body: "Access to the private area is through the back laneway off Eden Street. Follow it to the gate and you're in.",
  },
  {
    icon: Car,
    title: "Park at the Hyde Park Hotel",
    body: "Easiest parking is at the Hyde Park Hotel — leave the car there and it's a short walk up the laneway to the gate.",
  },
  {
    icon: KeyRound,
    title: "Your own PIN. No front desk.",
    body: "Private bookings are truly private — you don't have to interact with anyone to enter. Your reminder one hour before your session includes a temporary PIN that works on both the rear gate and the rear door — enter it separately on each keypad, and everything relocks normally behind you. The PIN is live from 15 minutes before your session until 10 minutes after it ends; nothing stays unlocked.",
  },
];

const Access = () => {
  return (
    <section id="access" data-testid="access-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <SectionHeading
            overline="Parking & access"
            title={
              <>
                Slip in the back. <em className="italic text-ember">Code in, code out.</em>
              </>
            }
          />
          <div className="mt-10 space-y-8">
            {points.map((p, i) => (
              <Reveal key={p.title} delay={0.08 + i * 0.07}>
                <div className="flex gap-4" data-testid={`access-point-${i + 1}`}>
                  <div className="shrink-0 flex h-10 w-10 items-center justify-center border border-rust/40 bg-rust/10">
                    <p.icon className="h-4 w-4 text-ember" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-cream">{p.title}</h3>
                    <p className="mt-1 text-sm md:text-base font-light leading-relaxed text-sand max-w-lg">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div data-testid="access-recovery-pop-note" className="mt-10 flex items-start gap-3 border border-line bg-coal p-5 max-w-lg">
              <Bike className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
              <p className="text-sm font-light leading-relaxed text-sand">
                <span className="text-cream font-medium">Recovery Pop Saturdays:</span>{" "}
                come straight in through the front — unless a Rhythm Ride class is
                running, in which case slip around to the rear laneway gate like
                everyone else.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="relative overflow-hidden border border-line group">
            <img
              src="/assets/gate-placeholder.jpg"
              alt="The gated laneway entrance to TEMPA off Eden Street"
              loading="lazy"
              data-testid="access-gate-photo"
              className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
              <div>
                <p className="font-display text-lg text-cream">The Eden Street gate</p>
                <p className="mt-0.5 text-xs tracking-[0.2em] uppercase text-stone2">Rear laneway · code entry</p>
              </div>
              <span
                data-testid="access-photo-placeholder-badge"
                className="flex items-center gap-1.5 border border-line bg-ink/85 backdrop-blur-md px-3 py-2 text-[10px] tracking-[0.15em] uppercase text-stone2"
              >
                <Camera className="h-3 w-3" aria-hidden="true" />
                Real gate photo coming
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Access;
